import { groupBy, upperFirst } from "lodash-es";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ActiveFilterBar } from "~/components/Filter/ActiveFilterBar/ActiveFilterBar";
import {
  ActiveFilterButton,
  ActiveFilterButtonProps,
} from "~/components/Filter/ActiveFilterButton/ActiveFilterButton";
import {
  FilterBar,
  FilterBarProps,
} from "~/components/Filter/FilterBar/FilterBar";
import {
  FilterForm,
  FilterFormProps,
} from "~/components/Filter/FilterForm/FilterForm";
import { QuickFilterBar } from "~/components/Filter/QuickFilterBar/QuickFilterBar";
import {
  QuickFilterButton,
  QuickFilterButtonProps,
} from "~/components/Filter/QuickFilterButton/QuickFilterButton";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import {
  AssetFilterType,
  GetWorkflowAssetsQueryVariables,
  ScoreRating,
  useGetAssetFilterSuggestionsLazyQuery,
} from "~/operations";
import { FilterMenuOption } from "~/components/Filter/FilterMenu";
import { truncateText } from "~/lib/truncateText";
import { addFilter, removeFilter, resetFilters } from "../utils";
import {
  emptyMenuOption,
  loadingMenuOption,
} from "~/components/Filter/QuickFilterMenu/QuickFilterMenu";

export type WorkflowAssetsQueryVariables = Pick<
  NonNullable<GetWorkflowAssetsQueryVariables>,
  "queryTerms" | "rating"
> & {
  queryTerms?: string[];
  rating?: ScoreRating[];
  labels?: string[];
  annotations?: string[];
};

export type UseWorkflowAssetsFilterContextParams = {
  scopeMrn: string;
  workflowMrn: string;
};

export type UseWorkflowAssetsFilterContextOutput = {
  filterVariables: WorkflowAssetsQueryVariables;
  filterForm: ReactNode;
  filterBar: ReactNode;
  quickFilterBar: ReactNode;
  activeFilterBar: ReactNode;
  activeFilterCount: number;
  resetFilters: () => void;
};

export function useWorkflowAssetsFilterContext({
  scopeMrn,
  workflowMrn, // we can't filter by workflow yet, but will need to in the future
}: UseWorkflowAssetsFilterContextParams): UseWorkflowAssetsFilterContextOutput {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // search text typed into search bar
  const [searchInput, setSearchInput] = useState("");

  const riskRatingFilterParams = searchParams.getAll("riskRating");
  const labelFilterParams = searchParams.getAll("label");
  const annotationFilterParams = searchParams.getAll("annotation");
  const searchFilterParams = searchParams.getAll("queryTerm");

  const riskRatingFilters = riskRatingFilterParams.every((riskFactorParam) =>
    isScoreRating(riskFactorParam),
  )
    ? riskRatingFilterParams
    : undefined;
  const labelFilters = labelFilterParams.length ? labelFilterParams : undefined;
  const annotationFilters = annotationFilterParams.length
    ? annotationFilterParams
    : undefined;
  const searchFilters = searchFilterParams.length
    ? searchFilterParams
    : undefined;

  const filterVariables: WorkflowAssetsQueryVariables = useMemo(() => {
    return {
      ...(riskRatingFilters?.length ? { rating: riskRatingFilters } : {}),
      ...(labelFilters?.length ? { labels: labelFilters } : {}),
      ...(annotationFilters?.length ? { annotations: annotationFilters } : {}),
      ...(searchFilters?.length ? { queryTerms: searchFilters } : {}),
    };
  }, [
    JSON.stringify(riskRatingFilterParams),
    JSON.stringify(labelFilters),
    JSON.stringify(annotationFilters),
    JSON.stringify(searchFilterParams),
  ]);

  const riskRatingSuggestions = useWorkflowAssetsRiskRatingSuggestions(
    scopeMrn,
    filterVariables,
  );

  const activeFilters = [
    riskRatingFilters
      ? riskRatingFilters.map((riskRatingFilter) => {
          return {
            label: "Risk Rating",
            value: formatRiskRatingLabel(riskRatingFilter),
            onClick: () =>
              removeFilter(navigate, "riskRating", riskRatingFilter),
          };
        })
      : [],
    labelFilters
      ? labelFilters.map((labelFilter) => {
          return {
            label: "Label",
            value: formatKeyValueLabel(labelFilter),
            onClick: () => removeFilter(navigate, "label", labelFilter),
          };
        })
      : [],
    annotationFilters
      ? annotationFilters.map((annotationFilter) => {
          return {
            label: "Annotation",
            value: formatKeyValueLabel(annotationFilter),
            onClick: () =>
              removeFilter(navigate, "annotation", annotationFilter),
          };
        })
      : [],
    searchFilters
      ? searchFilters.map((searchFilter) => {
          return {
            label: "Search",
            value: searchFilter,
            onClick: () => removeFilter(navigate, "queryTerm", searchFilter),
          };
        })
      : [],
  ].flatMap((f) => f);

  const [focusedField, setFocusedField] = useState<FilterMenuOption>();

  const fieldOptions = useWorkflowAssetsSuggestions(
    focusedField?.field || "",
    searchInput,
    scopeMrn,
    filterVariables,
  );

  useEffect(() => {
    if ("fetchSuggestions" in fieldOptions) {
      fieldOptions.fetchSuggestions();
    }
  }, [focusedField?.field]);

  const filterBar = renderFilterBar({
    fieldOptions: fieldOptions.suggestions,
    focusedField,
    inputValue: searchInput,
    onInputValueChange: (value: string) => {
      setSearchInput(value);
      if ("fetchSuggestions" in fieldOptions) {
        fieldOptions.fetchSuggestions(value);
      }
    },
    onFocusedRemove: () => {
      setFocusedField(undefined);
    },
    onFilterSelect: (filter) => {
      setSearchInput("");
      if (!focusedField) {
        setFocusedField(filter);
      } else {
        setFocusedField(undefined);
        if (filter.param) {
          addFilter(navigate, filter.param, filter.value, filter.isMultiple);
        }
      }
    },
    onFilterCreate: (filter) => {
      setFocusedField(undefined);
      setSearchInput("");
      addFilter(navigate, "queryTerm", filter, true);
    },
  });

  const quickMenuOptions = (param: string, options: FilterMenuOption[]) => {
    return options.map((o) => ({
      ...o,
      onSelect: () => addFilter(navigate, param, o.value, o.isMultiple),
    }));
  };

  const quickFilterBar = renderQuickFilterBar([
    {
      label: "Risk Rating",
      activeCount: riskRatingFilters?.length || 0,
      menuOptions: quickMenuOptions(
        "riskRating",
        riskRatingSuggestions.suggestions,
      ),
    },
  ]);

  const activeFilterBar = renderActiveFilterBar(activeFilters, () =>
    resetFilters(navigate),
  );

  const activeFilterCount = activeFilters.length;

  const filterForm = renderFilterForm({
    filterBar,
    quickFilterBar,
    activeFilterBar,
  });

  return {
    filterVariables,
    filterForm,
    filterBar,
    quickFilterBar,
    activeFilterBar,
    activeFilterCount,
    resetFilters: () => resetFilters(navigate),
  };
}

function renderFilterBar(props: FilterBarProps) {
  return (
    <FilterBar
      {...props}
      sx={{ backgroundColor: "background.paper", borderRadius: 2 }}
    />
  );
}

function renderQuickFilterBar(quickFilters: QuickFilterButtonProps[]) {
  return (
    <QuickFilterBar mt={0.5}>
      {quickFilters.map((quickFilterProps) => {
        const key = quickFilterProps.label;
        return (
          <QuickFilterButton
            key={key}
            {...quickFilterProps}
            sx={{ px: 1, py: 0.5, boxShadow: "none" }}
          />
        );
      })}
    </QuickFilterBar>
  );
}

function renderActiveFilterBar(
  activeFilters: ActiveFilterButtonProps[],
  onResetClick: () => void,
) {
  const items = Object.entries(groupBy(activeFilters, "label")).flatMap(
    ([label, values], index) => {
      const buttons = values.map((props) => {
        const key = props.label + props.value;
        return <ActiveFilterButton key={key} {...props} />;
      });
      const separator = (
        <ChevronRightIcon
          key={label + "-separator"}
          sx={{ fontSize: "16px", color: "text.secondary" }}
        />
      );
      return [index === 0 ? [] : separator, ...buttons];
    },
  );
  return (
    items.length > 0 && (
      <ActiveFilterBar onResetClick={onResetClick}>{items}</ActiveFilterBar>
    )
  );
}

function renderFilterForm(props: FilterFormProps) {
  return <FilterForm {...props} />;
}

function useWorkflowAssetsSuggestions(
  field: string,
  searchInput: string,
  scopeMrn: string,
  filterVars: WorkflowAssetsQueryVariables,
) {
  const categorySuggestions = useWorkflowAssetsCategorySuggestions(searchInput);
  const riskRatingSuggestions = useWorkflowAssetsRiskRatingSuggestions(
    scopeMrn,
    filterVars,
  );
  const labelSuggestions = useWorkflowAssetsLabelSuggestions(
    scopeMrn,
    filterVars,
  );
  const annotationSuggestions = useWorkflowAssetsAnnotationSuggestions(
    scopeMrn,
    filterVars,
  );
  switch (field) {
    case "riskRating":
      return riskRatingSuggestions;
    case "label":
      return labelSuggestions;
    case "annotation":
      return annotationSuggestions;
    default:
      return categorySuggestions;
  }
}

function useWorkflowAssetsCategorySuggestions(searchInput: string) {
  const riskRatingFilterGroup: FilterMenuOption = {
    field: "riskRating",
    label: "Risk Rating",
    value: "riskRating",
  };

  const labelFilterGroup: FilterMenuOption = {
    field: "label",
    label: "Label",
    value: "label",
  };

  const annotationFilterGroup: FilterMenuOption = {
    field: "annotation",
    label: "Annotation",
    value: "annotation",
  };

  const queryFilterGroup: FilterMenuOption = {
    field: "queryTerm",
    label: `Press enter to free text search "${searchInput}"`,
    value: "queryTerm",
    isDisabled: true,
  };

  const suggestions = [
    searchInput.length > 0 ? queryFilterGroup : undefined,
    riskRatingFilterGroup,
    labelFilterGroup,
    annotationFilterGroup,
  ]
    .flatMap((g) => g ?? [])
    .filter((s) =>
      s.label.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
    );
  const loading = false;
  const error = false;

  return {
    suggestions,
    loading,
    error,
  };
}

function useWorkflowAssetsRiskRatingSuggestions(
  scopeMrn: string,
  _filterVars: WorkflowAssetsQueryVariables,
) {
  const [getSuggestions, { data, loading, error, previousData }] =
    useGetAssetFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AssetFilterType.RiskRating,
        searchText,
        scopeMrn,
        // workflowMrn, // will need to add workflowMrn to all filters
      },
    });
  };

  const param = "riskRating";

  const suggestionsData =
    (data ?? previousData)?.assetFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "ScoreRatingValue"
            ? suggestion.value.ratingValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: formatRiskRatingLabel(suggestion.label),
          isMultiple: true,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useWorkflowAssetsLabelSuggestions(
  scopeMrn: string,
  filterVars: WorkflowAssetsQueryVariables,
) {
  const [getSuggestions, { data, loading, error, previousData }] =
    useGetAssetFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AssetFilterType.Labels,
        searchText,
        scopeMrn,
        filter: {
          rating: filterVars.rating,
        },
        limit: 25,
      },
    });
  };

  const param = "label";

  const suggestionsData =
    (data ?? previousData)?.assetFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "StringValue"
            ? suggestion.value.stringValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: suggestion.label,
          isMultiple: true,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useWorkflowAssetsAnnotationSuggestions(
  scopeMrn: string,
  filterVars: WorkflowAssetsQueryVariables,
) {
  const [getSuggestions, { data, loading, error, previousData }] =
    useGetAssetFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AssetFilterType.Annotations,
        searchText,
        scopeMrn,
        filter: {
          rating: filterVars.rating,
        },
        limit: 25,
      },
    });
  };

  const param = "annotation";

  const suggestionsData =
    (data ?? previousData)?.assetFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "StringValue"
            ? suggestion.value.stringValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: suggestion.label,
          isMultiple: true,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function isScoreRating(value: any): value is ScoreRating {
  return Object.values(ScoreRating).includes(value);
}

function formatRiskRatingLabel(label: string) {
  return upperFirst(label.toLocaleLowerCase());
}

// "{'distro-id': 'ubuntu'}" => "distro-id:ubuntu"
function formatKeyValueLabel(label: string) {
  try {
    const [keyValue]: [string, string][] = Object.entries(JSON.parse(label));
    const [key, value] = keyValue;
    return [truncateText(key, 20), truncateText(value, 20)].join(":");
  } catch (error) {
    return label;
  }
}
